import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from './AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';

// RevenueCat Entitlement ID - must match exactly what's in RevenueCat dashboard
export const PREMIUM_ENTITLEMENT_ID = 'Premium';

// RevenueCat Product IDs matching App Store Connect
export const REVENUECAT_PRODUCTS = {
  monthly: {
    productId: 'prod87b17835be',
    entitlementId: PREMIUM_ENTITLEMENT_ID,
    interval: 'month' as const,
  },
  yearly: {
    productId: 'prod6fa24a1ae5',
    entitlementId: PREMIUM_ENTITLEMENT_ID,
    interval: 'year' as const,
  },
} as const;

// RevenueCat API Key
const REVENUECAT_API_KEY = 'appl_vDFoquOwYWVLotdJfRoPCbhkcjd';

// Lazy import types to avoid loading the full SDK on web
type CustomerInfo = Awaited<ReturnType<typeof import('@revenuecat/purchases-capacitor').Purchases.getCustomerInfo>>['customerInfo'];
type OfferingsResult = Awaited<ReturnType<typeof import('@revenuecat/purchases-capacitor').Purchases.getOfferings>>;
type PurchasesPackage = NonNullable<OfferingsResult['current']>['availablePackages'][0];
type PurchasesApi = typeof import('@revenuecat/purchases-capacitor').Purchases;

export interface PurchaseDiagnostics {
  lastStep: string | null;
  lastError: string | null;
  lastPath: 'package' | 'storeProduct' | null;
  lastMatchedProductId: string | null;
  lastEntitlementKeys: string[];
  lastPremiumActive: boolean | null;
  attemptedAt: string | null;
}

interface RevenueCatContextValue {
  isNative: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  customerInfo: CustomerInfo | null;
  packages: PurchasesPackage[];
  error: string | null;
  diagnostics: PurchaseDiagnostics;
  
  // Entitlement checks
  isPremium: () => boolean;
  getExpirationDate: () => Date | null;
  getTrialEndDate: () => Date | null;
  getCurrentProductId: () => string | null;

  
  // Package helpers
  getPackageByTier: (tier: 'monthly' | 'yearly') => PurchasesPackage | null;
  getPriceString: (tier: 'monthly' | 'yearly') => string | null;
  getPricePerMonthString: (tier: 'monthly' | 'yearly') => string | null;
  
  // Actions
  purchasePackage: (pkg: PurchasesPackage) => Promise<boolean>;
  purchaseProduct: (tier: 'monthly' | 'yearly') => Promise<boolean>;
  restorePurchases: () => Promise<boolean>;
  refreshCustomerInfo: () => Promise<void>;
  refreshOfferings: () => Promise<void>;
}

const RevenueCatContext = createContext<RevenueCatContextValue | null>(null);

export function useRevenueCatContext() {
  const context = useContext(RevenueCatContext);
  if (!context) {
    throw new Error('useRevenueCatContext must be used within RevenueCatProvider');
  }
  return context;
}



interface RevenueCatState {
  isInitialized: boolean;
  isRevenueCatReady: boolean;
  isLoading: boolean;
  customerInfo: CustomerInfo | null;
  packages: PurchasesPackage[];
  error: string | null;
}

export function RevenueCatProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const isNative = Capacitor.isNativePlatform();
  
  const [state, setState] = useState<RevenueCatState>({
    isInitialized: false,
    isRevenueCatReady: false,
    isLoading: true,
    customerInfo: null,
    packages: [],
    error: null,
  });

  const [diagnostics, setDiagnostics] = useState<PurchaseDiagnostics>({
    lastStep: null,
    lastError: null,
    lastPath: null,
    lastMatchedProductId: null,
    lastEntitlementKeys: [],
    lastPremiumActive: null,
    attemptedAt: null,
  });

  const recordStep = useCallback((step: string, patch: Partial<PurchaseDiagnostics> = {}) => {
    if (import.meta.env.DEV) console.log('[RevenueCat][purchase]', step, patch);
    setDiagnostics(prev => ({ ...prev, lastStep: step, attemptedAt: new Date().toISOString(), ...patch }));
  }, []);
  
  const sdkConfiguredRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);
  // Note: do NOT store the Purchases plugin proxy in a ref that React might inspect.
  // The Capacitor plugin proxy on iOS treats any unknown method (including `.then`)
  // as a native invocation, which throws "Purchases.then() is not implemented on ios".
  // Instead, we re-import lazily inside each call site after configure() has resolved.
  const purchasesModuleRef = useRef<typeof import('@revenuecat/purchases-capacitor') | null>(null);

  const neutralizeThenable = (Purchases: PurchasesApi) => {
    // Capacitor's plugin proxy on iOS treats ANY unknown method (including
    // `.then`) as a native bridge invocation, which throws
    // "Purchases.then() is not implemented on ios". Define a no-op `then`
    // so accidental thenable assimilation (e.g. returning the proxy from
    // an async function) resolves harmlessly instead of hitting the bridge.
    try {
      if (!Object.prototype.hasOwnProperty.call(Purchases, 'then')) {
        Object.defineProperty(Purchases, 'then', {
          value: undefined,
          writable: true,
          configurable: true,
        });
      }
    } catch {
      // Best-effort: if the proxy is sealed, fall back to existing guards.
    }
  };

  const getReadyPurchases = useCallback(async (): Promise<{ Purchases: PurchasesApi } | null> => {
    if (!isNative) return null;
    if (!sdkConfiguredRef.current) return null;
    if (purchasesModuleRef.current) return { Purchases: purchasesModuleRef.current.Purchases };
    try {
      const mod = await import('@revenuecat/purchases-capacitor');
      purchasesModuleRef.current = mod;
      neutralizeThenable(mod.Purchases);
      return { Purchases: mod.Purchases };
    } catch (e) {
      console.error('[RevenueCat] Failed to re-load module:', e);
      return null;
    }
  }, [isNative]);

  // Configure SDK once on mount (native only)
  useEffect(() => {
    if (!isNative || sdkConfiguredRef.current) return;
    
    let mounted = true;
    
    const configureSDK = async () => {
      try {
        const mod = await import('@revenuecat/purchases-capacitor');
        if (!mounted) return;
        purchasesModuleRef.current = mod;
        const Purchases = mod.Purchases;
        if (!Purchases) {
          throw new Error('Purchases plugin not available');
        }
        neutralizeThenable(Purchases);

        // Small delay to ensure native bridge is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        await Purchases.configure({ apiKey: REVENUECAT_API_KEY });
        
        sdkConfiguredRef.current = true;
        
        if (mounted) {
          setState(prev => ({
            ...prev,
            isInitialized: true,
            isRevenueCatReady: true,
          }));
        }
      } catch (error: any) {
        console.error('[RevenueCat] SDK configuration error:', error);
        if (mounted) {
          setState(prev => ({
            ...prev,
            isInitialized: true,
            isRevenueCatReady: false,
            isLoading: false,
            error: error?.message || 'Failed to configure SDK',
          }));
        }
      }
    };
    
    configureSDK();
    
    return () => { mounted = false; };
  }, [isNative]);

  // Sync RevenueCat identity with auth user
  useEffect(() => {
    // Skip if not native, not configured/ready, or auth still loading
    if (!isNative || !sdkConfiguredRef.current || !state.isRevenueCatReady || authLoading) return;
    
    let mounted = true;
    
    const syncUser = async () => {
      const purchasesApi = await getReadyPurchases();
      if (!purchasesApi) return;
      const { Purchases } = purchasesApi;
      
      const previousUserId = currentUserIdRef.current;
      const newUserId = user?.id || null;
      
      // Skip if user hasn't changed
      if (previousUserId === newUserId) {
        // Still need to fetch customer info if we don't have it
        if (newUserId && !state.customerInfo) {
          try {
            setState(prev => ({ ...prev, isLoading: true }));
            const { customerInfo } = await Purchases.getCustomerInfo();
            const offeringsResult = await Purchases.getOfferings();
            const packages = offeringsResult?.current?.availablePackages || [];
            
            if (mounted) {
              setState(prev => ({
                ...prev,
                customerInfo,
                packages,
                isLoading: false,
              }));
            }
          } catch (error) {
            console.error('[RevenueCat] Failed to fetch customer info:', error);
            if (mounted) {
              setState(prev => ({ ...prev, isLoading: false }));
            }
          }
        } else if (!newUserId) {
          setState(prev => ({ ...prev, isLoading: false }));
        }
        return;
      }
      
      currentUserIdRef.current = newUserId;
      setState(prev => ({ ...prev, isLoading: true }));
      
      try {
        if (newUserId) {
          // User logged in - identify with RevenueCat.
          // Wrap logIn so that a network/SDK failure can't leave the paywall
          // stuck on a forever-loading state — we'll still try to read
          // entitlements from the cached customer info below.
          try {
            await Purchases.logIn({ appUserID: newUserId });
          } catch (logInErr) {
            console.error('[RevenueCat] Purchases.logIn failed:', logInErr);
          }

          // Fetch fresh customer info for this user
          let { customerInfo } = await Purchases.getCustomerInfo();

          // If the freshly-logged-in user has no Premium entitlement, attempt
          // a one-shot restore. This recovers paid users whose receipt was
          // attached to the previous anonymous RevenueCat identity (e.g.
          // purchase completed before sign-in, or app reinstalled).
          const hasPremium =
            customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID]?.isActive === true;
          if (!hasPremium) {
            try {
              const restored = await Purchases.restorePurchases();
              if (restored?.customerInfo) {
                customerInfo = restored.customerInfo;
              }
            } catch (restoreErr) {
              console.warn('[RevenueCat] Auto-restore on login failed:', restoreErr);
            }
          }

          // Fetch offerings (with one automatic retry — App Store can be slow
          // on cold start and an empty first response would leave the paywall
          // permanently locked until the user manually taps Try again).
          let offeringsResult = await Purchases.getOfferings();
          let packages = offeringsResult?.current?.availablePackages || [];

          if (packages.length === 0) {
            console.warn('[RevenueCat] Empty offerings on first try, retrying in 1.5s…', {
              hasCurrentOffering: !!offeringsResult?.current,
              currentOfferingId: offeringsResult?.current?.identifier ?? null,
              allOfferingIds: Object.keys(offeringsResult?.all ?? {}),
            });
            await new Promise(r => setTimeout(r, 1500));
            try {
              offeringsResult = await Purchases.getOfferings();
              packages = offeringsResult?.current?.availablePackages || [];
            } catch (retryErr) {
              console.error('[RevenueCat] Retry getOfferings failed:', retryErr);
            }
          }

          if (mounted) {
            setState(prev => ({
              ...prev,
              customerInfo,
              packages,
              isLoading: false,
              error: null,
            }));
            
            // Invalidate subscription queries to refresh UI
            queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
          }
        } else {
          // User logged out - clear RevenueCat identity
          await Purchases.logOut();
          
          if (mounted) {
            // Clear customer info immediately
            setState(prev => ({
              ...prev,
              customerInfo: null,
              isLoading: false,
              error: null,
            }));
            
            // Invalidate subscription queries
            queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
          }
        }
      } catch (error: any) {
        console.error('[RevenueCat] User sync error:', error);
        if (mounted) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: error?.message || 'Failed to sync user',
          }));
        }
      }
    };
    
    syncUser();
    
    return () => { mounted = false; };
  }, [isNative, user?.id, authLoading, queryClient, getReadyPurchases, state.isRevenueCatReady]);

  // Non-native: mark as ready immediately
  useEffect(() => {
    if (!isNative) {
      setState(prev => ({
        ...prev,
        isInitialized: true,
        isLoading: false,
      }));
    }
  }, [isNative]);

  // Check if user has premium entitlement
  const isPremium = useCallback(() => {
    if (!state.customerInfo) return false;
    return state.customerInfo.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID]?.isActive === true;
  }, [state.customerInfo]);

  // Whenever RevenueCat customerInfo updates (launch / restore / purchase),
  // mirror the premium status to profiles.is_subscribed via the
  // sync-revenuecat-entitlement edge function. We CANNOT update is_subscribed
  // directly from the client — the protect_profile_subscription_columns DB
  // trigger blocks any authenticated client write, so a direct update would
  // silently fail and leave paying users locked out of every server-enforced
  // premium gate (routine limits, recipe limits, coach chat caps, etc.).
  const syncEntitlementToServer = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { error } = await supabase.functions.invoke(
        'sync-revenuecat-entitlement',
        { body: {} },
      );
      if (error) {
        console.error(
          '[RevenueCat] sync-revenuecat-entitlement failed:',
          error,
        );
        return;
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
    } catch (err) {
      console.error(
        '[RevenueCat] sync-revenuecat-entitlement threw:',
        err,
      );
    }
  }, [user?.id, queryClient]);

  useEffect(() => {
    if (!isNative) return;
    if (!user?.id) return;
    if (!state.customerInfo) return;
    syncEntitlementToServer();
  }, [isNative, user?.id, state.customerInfo, syncEntitlementToServer]);

  // Force refresh on every app foreground / window focus so paid users never
  // see stale "Go Premium" UI after a purchase, restore, or cancellation.
  useEffect(() => {
    if (!isNative || !user?.id) return;
    const handleFocus = async () => {
      try {
        const purchasesApi = await getReadyPurchases();
        if (!purchasesApi) return;
        const { Purchases } = purchasesApi;
        const { customerInfo } = await Purchases.getCustomerInfo();
        setState(prev => ({ ...prev, customerInfo }));
        // Also re-verify server-side so profiles.is_subscribed reflects
        // whatever state the App Store gave us on resume.
        syncEntitlementToServer();
      } catch (err) {
        // Non-fatal — next app launch will resync
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [isNative, user?.id, getReadyPurchases, syncEntitlementToServer]);


  // Get subscription expiration date
  const getExpirationDate = useCallback(() => {
    if (!state.customerInfo) return null;
    const premiumEntitlement = state.customerInfo.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID];
    return premiumEntitlement?.expirationDate ? new Date(premiumEntitlement.expirationDate) : null;
  }, [state.customerInfo]);

  // If the active entitlement is currently in its trial period, return the
  // trial's expiration date. Returns null when the user is on a paid period,
  // not subscribed, or RevenueCat hasn't loaded yet.
  const getTrialEndDate = useCallback(() => {
    if (!state.customerInfo) return null;
    const premiumEntitlement = state.customerInfo.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID] as
      | { isActive?: boolean; periodType?: string; expirationDate?: string | null }
      | undefined;
    if (!premiumEntitlement?.isActive) return null;
    const isTrial =
      typeof premiumEntitlement.periodType === 'string' &&
      premiumEntitlement.periodType.toUpperCase() === 'TRIAL';
    if (!isTrial || !premiumEntitlement.expirationDate) return null;
    return new Date(premiumEntitlement.expirationDate);
  }, [state.customerInfo]);


  // Get current product ID
  const getCurrentProductId = useCallback(() => {
    if (!state.customerInfo) return null;
    const premiumEntitlement = state.customerInfo.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID];
    return premiumEntitlement?.productIdentifier || null;
  }, [state.customerInfo]);

  const packageMatchesTier = useCallback((pkg: PurchasesPackage, tier: 'monthly' | 'yearly') => {
    const productId = REVENUECAT_PRODUCTS[tier].productId;
    const expectedPackageIdentifier = tier === 'monthly' ? '$rc_monthly' : '$rc_annual';
    const expectedPackageTypes = tier === 'monthly' ? ['MONTHLY', 'monthly'] : ['ANNUAL', 'annual', 'YEARLY', 'yearly'];

    return (
      pkg.product?.identifier === productId ||
      pkg.identifier === expectedPackageIdentifier ||
      expectedPackageTypes.includes(String(pkg.packageType))
    );
  }, []);

  // Get package by tier with fallback to package identifier / packageType
  const getPackageByTier = useCallback((tier: 'monthly' | 'yearly'): PurchasesPackage | null => {
    return state.packages.find((pkg) => packageMatchesTier(pkg, tier)) || null;
  }, [packageMatchesTier, state.packages]);

  // Get localized price string for a tier
  const getPriceString = useCallback((tier: 'monthly' | 'yearly'): string | null => {
    const pkg = getPackageByTier(tier);
    return pkg?.product?.priceString || null;
  }, [getPackageByTier]);

  // Get price per month string
  const getPricePerMonthString = useCallback((tier: 'monthly' | 'yearly'): string | null => {
    const pkg = getPackageByTier(tier);
    return (pkg?.product as any)?.pricePerMonthString || null;
  }, [getPackageByTier]);

  // Purchase a package
  const purchasePackage = useCallback(async (pkg: PurchasesPackage): Promise<boolean> => {
    const purchasesApi = await getReadyPurchases();
    if (!purchasesApi) {
      throw new Error('Purchases not ready');
    }
    const { Purchases } = purchasesApi;

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      setState(prev => ({ ...prev, customerInfo, isLoading: false }));
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
      return customerInfo.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID]?.isActive === true;
    } catch (error: any) {
      setState(prev => ({ ...prev, isLoading: false }));
      if (error?.code === 1 || error?.userCancelled) {
        return false;
      }
      throw error;
    }
  }, [getReadyPurchases, queryClient]);

  // Purchase by product tier
  const purchaseProduct = useCallback(async (tier: 'monthly' | 'yearly'): Promise<boolean> => {
    setDiagnostics({
      lastStep: 'starting',
      lastError: null,
      lastPath: null,
      lastMatchedProductId: null,
      lastEntitlementKeys: [],
      lastPremiumActive: null,
      attemptedAt: new Date().toISOString(),
    });
    recordStep(`tier=${tier}`);

    const purchasesApi = await getReadyPurchases();
    if (!purchasesApi) {
      recordStep('error:purchases-not-ready', { lastError: 'Purchases not ready' });
      throw new Error('Purchases not ready');
    }
    const { Purchases } = purchasesApi;
    recordStep('purchases-ready');

    let targetPackage = getPackageByTier(tier);
    if (targetPackage) {
      recordStep('matched-cached-package', {
        lastMatchedProductId: targetPackage.product?.identifier ?? null,
      });
    }

    // If not found in current state, refresh offerings (flatten current + all)
    if (!targetPackage) {
      recordStep('refreshing-offerings');
      try {
        const offeringsResult = await Purchases.getOfferings();
        const currentPkgs = offeringsResult?.current?.availablePackages || [];
        const allPkgs: PurchasesPackage[] = [];
        const allMap = offeringsResult?.all ?? {};
        for (const key of Object.keys(allMap)) {
          const offer = (allMap as any)[key];
          if (offer?.availablePackages) allPkgs.push(...offer.availablePackages);
        }
        const seen = new Set<string>();
        const merged = [...currentPkgs, ...allPkgs].filter((p) => {
          const id = p?.product?.identifier ?? p?.identifier;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        recordStep(`offerings-flattened count=${merged.length}`);
        if (merged.length > 0) {
          setState(prev => ({ ...prev, packages: merged }));
        }
        targetPackage = merged.find((pkg) => packageMatchesTier(pkg, tier)) || null;
        if (targetPackage) {
          recordStep('matched-after-refresh', {
            lastMatchedProductId: targetPackage.product?.identifier ?? null,
          });
        }
      } catch (err: any) {
        console.error('[RevenueCat] Tap-time getOfferings failed:', err);
        recordStep('error:getOfferings', { lastError: err?.message ?? String(err) });
      }
    }

    // Last resort: fetch StoreKit product directly by ID and purchase it
    if (!targetPackage) {
      const productId = REVENUECAT_PRODUCTS[tier].productId;
      recordStep(`fallback-getProducts ${productId}`);
      try {
        const result: any = await (Purchases as any).getProducts({ productIdentifiers: [productId] });
        const storeProduct = result?.products?.[0];
        recordStep(`getProducts-result found=${!!storeProduct}`);
        if (storeProduct) {
          recordStep('purchaseStoreProduct-call', { lastPath: 'storeProduct', lastMatchedProductId: storeProduct?.identifier ?? productId });
          setState(prev => ({ ...prev, isLoading: true, error: null }));
          try {
            const purchaseResult: any = await (Purchases as any).purchaseStoreProduct({ product: storeProduct });
            const customerInfo = purchaseResult?.customerInfo;
            const keys = Object.keys(customerInfo?.entitlements?.active ?? {});
            const premiumActive = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID]?.isActive === true;
            recordStep(`storeProduct-result premiumActive=${premiumActive}`, {
              lastEntitlementKeys: keys,
              lastPremiumActive: premiumActive,
            });
            if (customerInfo) {
              setState(prev => ({ ...prev, customerInfo, isLoading: false }));
              queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
              return premiumActive;
            }
          } catch (error: any) {
            setState(prev => ({ ...prev, isLoading: false, error: error?.message }));
            const cancelMsgSk = (error?.message || error?.underlyingErrorMessage || '').toString().toLowerCase();
            if (error?.code === 1 || error?.code === '1' || error?.userCancelled === true || cancelMsgSk.includes('cancel')) {
              recordStep('storeProduct-cancelled', { lastError: 'user cancelled' });
              return false;
            }
            recordStep('error:purchaseStoreProduct', { lastError: error?.message ?? String(error) });
            throw error;
          } finally {
            setState(prev => ({ ...prev, isLoading: false }));
          }
        }
      } catch (err: any) {
        console.error('[RevenueCat] getProducts fallback failed:', err);
        recordStep('error:getProducts', { lastError: err?.message ?? String(err) });
      }
    }

    if (!targetPackage) {
      recordStep('error:no-product-found', { lastError: `Product for ${tier} not found in offerings` });
      throw new Error(`Product for ${tier} not found in offerings`);
    }

    recordStep('purchasePackage-call', { lastPath: 'package', lastMatchedProductId: targetPackage.product?.identifier ?? null });
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: targetPackage });
      const keys = Object.keys(customerInfo?.entitlements?.active ?? {});
      const premiumActive = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID]?.isActive === true;
      recordStep(`purchasePackage-result premiumActive=${premiumActive}`, {
        lastEntitlementKeys: keys,
        lastPremiumActive: premiumActive,
      });
      setState(prev => ({ ...prev, customerInfo, isLoading: false }));
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
      return premiumActive;
    } catch (error: any) {
      console.error('[RevenueCat] Purchase error:', error);
      setState(prev => ({ ...prev, isLoading: false, error: error?.message }));
      const cancelMsg = (error?.message || error?.underlyingErrorMessage || '').toString().toLowerCase();
      if (error?.code === 1 || error?.code === '1' || error?.userCancelled === true || cancelMsg.includes('cancel')) {
        recordStep('purchasePackage-cancelled', { lastError: 'user cancelled' });
        return false;
      }
      recordStep('error:purchasePackage', { lastError: error?.message ?? String(error) });
      throw error;
    }
  }, [getPackageByTier, getReadyPurchases, packageMatchesTier, queryClient, recordStep]);

  // Restore purchases - only restores to current logged-in user
  const restorePurchases = useCallback(async (): Promise<boolean> => {
    const purchasesApi = await getReadyPurchases();
    if (!purchasesApi) {
      throw new Error('Purchases not ready');
    }
    const { Purchases } = purchasesApi;

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      // restorePurchases will fetch purchases for the currently logged-in RevenueCat user
      // Since we log in with the Supabase user ID, this ensures restoration is user-specific
      const { customerInfo } = await Purchases.restorePurchases();
      setState(prev => ({ ...prev, customerInfo, isLoading: false }));
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
      return customerInfo.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID]?.isActive === true;
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [getReadyPurchases, queryClient]);

  // Refresh customer info
  const refreshCustomerInfo = useCallback(async () => {
    const purchasesApi = await getReadyPurchases();
    if (!purchasesApi) return;
    const { Purchases } = purchasesApi;

    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      setState(prev => ({ ...prev, customerInfo }));
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionStatus });
    } catch (error) {
      console.error('[RevenueCat] Failed to refresh customer info:', error);
    }
  }, [getReadyPurchases, queryClient]);

  // Refresh offerings
  const refreshOfferings = useCallback(async () => {
    const purchasesApi = await getReadyPurchases();
    if (!purchasesApi) return;
    const { Purchases } = purchasesApi;

    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const offeringsResult = await Purchases.getOfferings();
      const currentPkgs = offeringsResult?.current?.availablePackages || [];
      const allPkgs: PurchasesPackage[] = [];
      const allMap = offeringsResult?.all ?? {};
      for (const key of Object.keys(allMap)) {
        const offer = (allMap as any)[key];
        if (offer?.availablePackages) allPkgs.push(...offer.availablePackages);
      }
      const seen = new Set<string>();
      const packages = [...currentPkgs, ...allPkgs].filter((p) => {
        const id = p?.product?.identifier ?? p?.identifier;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      setState(prev => ({
        ...prev,
        packages,
        isLoading: false,
      }));

    } catch (error: any) {
      console.error('[RevenueCat] Refresh offerings error:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [getReadyPurchases]);

  const value: RevenueCatContextValue = {
    isNative,
    isInitialized: state.isInitialized,
    isLoading: state.isLoading,
    customerInfo: state.customerInfo,
    packages: state.packages,
    error: state.error,
    diagnostics,
    
    isPremium,
    getExpirationDate,
    getTrialEndDate,
    getCurrentProductId,

    
    getPackageByTier,
    getPriceString,
    getPricePerMonthString,
    
    purchasePackage,
    purchaseProduct,
    restorePurchases,
    refreshCustomerInfo,
    refreshOfferings,
  };

  return (
    <RevenueCatContext.Provider value={value}>
      {children}
    </RevenueCatContext.Provider>
  );
}
